"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { useAuth } from "@/contexts/AuthContext";
import type { Folders, FileItem } from "@/types/types";
import { foldersService } from "@/lib/services/folders.service";

type EditItemInput = { name: string; tags?: string };

export interface FoldersData {
  folders: Folders[];
  isFetchingFolders: boolean;
  foldersError: unknown;
  refetchFolders: () => Promise<Folders[]>;
  createFolder: (name: string, parentFolderId: number) => Promise<void>;
  editItem: (item: Folders | FileItem, input: EditItemInput) => Promise<void>;
  moveItem: (item: Folders | FileItem, destinationFolderId: number) => Promise<void>;
  deleteItem: (item: Folders | FileItem) => Promise<void>;
  addFilesToFolder: (files: File[], folderId: number, tags: string) => Promise<void>;
}

export function useFoldersData(): FoldersData {
  const { isLoggedIn, currentTenantId, userLang } = useAuth();
  const t = useTranslations();

  const [folders, setFolders] = useState<Folders[]>([]);
  const [isFetchingFolders, setIsFetchingFolders] = useState(false);
  const [foldersError, setFoldersError] = useState<unknown>(null);

  const tenantId = currentTenantId ? Number(currentTenantId) : null;
  const canFetch = isLoggedIn && !!tenantId;

  const fetchAndSet = useCallback(async (): Promise<Folders[]> => {
    if (!canFetch || !tenantId) {
      setFolders([]);
      setFoldersError(null);
      return [];
    }

    setIsFetchingFolders(true);
    setFoldersError(null);

    try {
      const data = await foldersService.fetchFolders({ isLoggedIn, tenantId, userLang });
      setFolders(data);
      return data;
    } catch (err) {
      setFoldersError(err);
      setFolders([]);

      toast.error(t("toast.folders.fetch-error-title", { defaultMessage: "Error loading folders" }), {
        description: t("toast.folders.fetch-error-desc", { defaultMessage: "Please try again." }),
        descriptionClassName: "!text-gray-800 dark:!text-gray-200",
      });

      return [];
    } finally {
      setIsFetchingFolders(false);
    }
  }, [canFetch, tenantId, isLoggedIn, userLang, t]);

  const refetchFolders = useCallback(() => fetchAndSet(), [fetchAndSet]);

  const createFolder = useCallback(async (name: string, parentFolderId: number) => {
    if (!tenantId) return;
    await foldersService.createFolder({ name, parentFolderId, tenantId });
    await refetchFolders();
  }, [tenantId, refetchFolders]);

  const editItem = useCallback(async (item: Folders | FileItem, input: EditItemInput) => {
    await foldersService.editItem({ item, name: input.name, tags: input.tags });
    await refetchFolders();
  }, [refetchFolders]);

  const moveItem = useCallback(async (item: Folders | FileItem, destinationFolderId: number) => {
    await foldersService.moveItem({ item, destinationFolderId });
    await refetchFolders();
  }, [refetchFolders]);

  const deleteItem = useCallback(async (item: Folders | FileItem) => {
    await foldersService.deleteItem({ item });
    await refetchFolders();
  }, [refetchFolders]);

  const addFilesToFolder = useCallback(async (files: File[], folderId: number, tags: string) => {
    if (!tenantId) return;
    await foldersService.addFilesToFolder({ files, folderId, tags, tenantId });
    await refetchFolders();
  }, [tenantId, refetchFolders]);

  useEffect(() => {
    if (!canFetch || !tenantId) {
      setFolders([]);
      setFoldersError(null);
      return;
    }

    void fetchAndSet();
  }, [canFetch, tenantId, fetchAndSet]);

  return useMemo(() => ({
    folders,
    isFetchingFolders,
    foldersError,
    refetchFolders,
    createFolder,
    editItem,
    moveItem,
    deleteItem,
    addFilesToFolder,
  }), [
    folders,
    isFetchingFolders,
    foldersError,
    refetchFolders,
    createFolder,
    editItem,
    moveItem,
    deleteItem,
    addFilesToFolder,
  ]);
}